import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Newspaper, ChevronLeft, ChevronRight, Filter, Calendar, Image as ImageIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { adminApi } from '@/services/adminApi';
import LoadingSpinner from '@/components/common/LoadingSpinner';

interface ContentBatch {
  batchId: string;
  weekStartDate: string;
  planSummary: string | null;
  status: string;
  totalPosts: number;
  imagesRendered: number;
  notificationSentAt: string | null;
  createdAt: string;
  finalizedAt: string | null;
  platforms: Record<string, number>;
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  pending_review: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  approved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  scheduled: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  published: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const PLATFORM_LABELS: Record<string, string> = {
  facebook: 'FB',
  instagram: 'IG',
  instagram_story: 'Story',
  twitter: 'X',
  twitter_thread: 'Thread',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[status] || STATUS_COLORS.draft}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

function PlatformPills({ platforms }: { platforms: Record<string, number> }) {
  return (
    <div className="flex flex-wrap gap-1">
      {Object.entries(platforms).map(([platform, count]) => (
        <span
          key={platform}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground"
        >
          {PLATFORM_LABELS[platform] || platform} {count}
        </span>
      ))}
    </div>
  );
}

export function ContentPage() {
  const [batches, setBatches] = useState<ContentBatch[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const limit = 20;

  useEffect(() => {
    loadBatches();
  }, [page, statusFilter]);

  const loadBatches = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminApi.getContentBatches({
        status: statusFilter || undefined,
        page,
        limit,
      });
      setBatches(data.batches);
      setTotal(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Content</h1>
          <p className="text-muted-foreground mt-1">
            Review and approve weekly social media content batches
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              aria-label="Filter by status"
            >
              <option value="">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="pending_review">Pending Review</option>
              <option value="approved">Approved</option>
              <option value="scheduled">Scheduled</option>
              <option value="published">Published</option>
              <option value="failed">Failed</option>
            </select>
            <span className="text-sm text-muted-foreground">
              {total} batch{total !== 1 ? 'es' : ''}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Content */}
      {loading ? (
        <LoadingSpinner message="Loading content batches..." />
      ) : error ? (
        <div className="text-center py-12">
          <p className="text-destructive mb-4">{error}</p>
          <button onClick={loadBatches} className="text-primary hover:underline">Retry</button>
        </div>
      ) : batches.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Newspaper className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg font-medium text-foreground">No content batches yet</p>
            <p className="text-muted-foreground mt-1">
              Content batches will appear here when Cowork generates weekly content.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Week</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Posts</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Platforms</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Images</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map((batch) => (
                    <tr key={batch.batchId} className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors">
                      <td className="py-3 px-4">
                        <Link
                          to={`/content/${batch.batchId}`}
                          className="font-medium text-primary hover:underline flex items-center gap-2"
                        >
                          <Calendar className="w-4 h-4" />
                          {batch.weekStartDate}
                        </Link>
                        {batch.planSummary && (
                          <p className="text-xs text-muted-foreground mt-1 truncate max-w-xs">
                            {batch.planSummary}
                          </p>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <StatusBadge status={batch.status} />
                      </td>
                      <td className="py-3 px-4 font-medium">{batch.totalPosts}</td>
                      <td className="py-3 px-4">
                        <PlatformPills platforms={batch.platforms} />
                      </td>
                      <td className="py-3 px-4">
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <ImageIcon className="w-3.5 h-3.5" />
                          {batch.imagesRendered}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">
                        {new Date(batch.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-lg hover:bg-muted disabled:opacity-50"
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-2 rounded-lg hover:bg-muted disabled:opacity-50"
                    aria-label="Next page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
